import * as yup from "yup";
import { useQuery } from "@tanstack/react-query";
import { HTTP_ENDPOINTS, QUERY_KEYS } from "../../consts/api";
import { http } from "../../services/HttpService";
import { SURVEY_TYPE } from "./config";

const requestSchema = yup.object({
  hash: yup.string().required(),
  accountId: yup.number().required(),
});

const responseSchema = yup.object({
  id: yup.number().nullable(),
  title: yup.string().nullable(),
  accountId: yup.number().nullable(),
  accountLogoUrl: yup.string().nullable(),
  obUrl: yup.string().nullable(),
  patientId: yup.number().nullable(),
  finished: yup.boolean().nullable(),
  deleted: yup.boolean().nullable(),
  questions: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        type: yup.string().required(),
        isRequired: yup.boolean().required(),
        description: yup.string().nullable(),
        question: yup.string().nullable(),
        options: yup
          .array()
          .of(
            yup.object({
              id: yup.number().required(),
              text: yup.string().nullable(),
            }),
          )
          .required(),
        allowComment: yup.boolean().required(),
      }),
    )
    .required(),
});

const composeResponse = (res) => {
  const data = res?.data?.data || {};
  const message = res?.data?.message || "";
  const isSubmitted = message === "survey_submitted";
  const isDeleted = message === "survey_deleted";

  return {
    id: data.survey_detail?.id,
    title: data.survey_detail?.title,
    accountId: data?.account_id,
    accountLogoUrl: data?.accountLogo,
    obUrl: data?.online_booking_url,
    patientId: data?.patient_id,
    finished: isSubmitted || Boolean(data.survey_detail?.status),
    deleted: isDeleted,
    questions:
      data.survey_questions
        ?.map((q) => ({
          id: q.id,
          type:
            q.question_type === "scale" ? SURVEY_TYPE.scale : q.question_type,
          isRequired: Boolean(q.required),
          description: q.description ? q.description_text : "",
          question: q.question,
          options: q.survey_question_choice || [],
          allowComment: Boolean(q.why_choose),
        }))
        ?.filter((q) => Object.values(SURVEY_TYPE).includes(q.type)) || [],
  };
};

export function usePublicSurveyQuery(payload, options = {}) {
  return useQuery(
    [QUERY_KEYS.surveys.public, payload.hash],
    async () => {
      const { hash } = requestSchema.validateSync(payload, {
        strict: true,
      });
      const res = await http.get(HTTP_ENDPOINTS.surveys.public.get(hash), {
        params: {
          accountId: payload.accountId,
        },
      });
      return responseSchema.validateSync(composeResponse(res), {
        strict: true,
      });
    },
    options,
  );
}
